<script setup>
import {computed, ref, watch} from 'vue'
import {useRoute} from 'vue-router'
import {useAppStore} from '@/stores/appStore'
import {useComponentsStore} from '@/stores/components'
import {useToastsStore} from '@/stores/toasts'
import {useUserStore} from '@/stores/user'
import {useItemDataStore} from '@/stores/itemData'
import {useViewStore} from '@/stores/view'
import api from '@/services/api'

// Define Props
const props = defineProps({
    viewId: String,
})

// Router Composable
const route = useRoute()

// Dynamic View Store
const viewStore = useViewStore(props.viewId)

// Pinia Stores
const appStore = useAppStore()
const componentsStore = useComponentsStore()
const toastsStore = useToastsStore()
const userStore = useUserStore()
const itemDataStore = useItemDataStore()

// Local Reactive State
const productTitle = ref('')

const listingName = computed(() => viewStore.listingName)
const tableName = computed(() => viewStore.tableName)
const allIds = computed(() => viewStore.allIds || [])
const productId = computed(() => parseInt(route.query.productId))

const hasExportAccess = computed(() => {
    return tableName.value ? userStore.access(tableName.value, 'export') : false
})

// Watch productId to fetch productTitle natively
watch(
    productId,
    async (newProductId) => {
        if (newProductId) {
            const item = await itemDataStore.get({
                tableName: 'm_products_products',
                id: newProductId
            })
            productTitle.value = item ? item.title : ''
        } else {
            productTitle.value = ''
        }
    },
    {immediate: true}
)

const exportCsv = async () => {
    if (confirm('Are you sure?\nPlease be patient as this may take a minute.')) {

        let exportFileName = ''
        if (tableName.value.indexOf('product_variations__') === 0) {
            exportFileName = productTitle.value
        } else {
            const component = componentsStore.items.find(o => o.tableName === tableName.value)
            exportFileName = component ? component.title : ''
        }

        // Generate a notification before we start the process.
        const {notificationId} = await toastsStore.notification({
            title: exportFileName + ' export',
            body: 'Starting&hellip;',
            downloadUrl: '',
            closeOnDownload: true
        })

        try {
            await api.post(`${appStore.apiEndpoint}/api/export`, {
                notificationId,
                buildName: appStore.sitename,
                tableName: tableName.value,
                ids: allIds.value,
                title: exportFileName,
            })
        } catch (error) {
            console.error('Export failed:', error)
        }
    }
}
</script>

<template>
    <button v-if="hasExportAccess" class="d-flex align-items-center" type="button" @click="exportCsv">
        <i class="me-2 bi-upload"/>
        Export CSV
    </button>
</template>